import * as v from 'valibot';

/**
 * Permission levels are totally ordered. `resolveLevel` relies on that order:
 * when a user is granted `read` directly but belongs to a group granted
 * `admin`, the highest grant wins.
 */
export const PermissionLevel = v.picklist(['read', 'edit', 'admin']);
export type PermissionLevel = v.InferOutput<typeof PermissionLevel>;

const RANK: Record<PermissionLevel, number> = { read: 1, edit: 2, admin: 3 };

/**
 * Who a grant can name.
 *
 * `agent` is a workspace agent identity — the principal behind what used to be
 * only a deployment secret. It is here rather than in a parallel structure
 * because the alternative was a second grant list with its own resolution
 * order, and two ways to say "this identity may reach this workflow" is one way
 * too many: the second one is the one nobody remembers to check.
 */
export const PrincipalKind = v.picklist(['user', 'group', 'agent']);
export type PrincipalKind = v.InferOutput<typeof PrincipalKind>;

export const SharingGrant = v.object({
  principal: PrincipalKind,
  /** Reef user id, the SCIM-synced group id, or the workspace agent id. */
  id: v.string(),
  level: PermissionLevel,
  /** Who created the grant, for the audit trail. */
  grantedBy: v.optional(v.string()),
  grantedAt: v.optional(v.string()),
});
export type SharingGrant = v.InferOutput<typeof SharingGrant>;

export function rankOf(level: PermissionLevel): number {
  return RANK[level];
}

export function atLeast(actual: PermissionLevel, required: PermissionLevel): boolean {
  return RANK[actual] >= RANK[required];
}

/**
 * One principal, as an access check sees it.
 *
 * Exactly one of `userId` and `agentId` is set by every caller in reef, and the
 * asymmetry is load-bearing: a subject carrying both would union a person's
 * grants with an agent's, which is precisely the widening an agent identity
 * must never be a route to. `@reef/authz` is where that is enforced — this type
 * only makes it expressible, so the rule has somewhere to be stated once.
 */
export interface GrantSubject {
  /** Reef user id, when a person is asking. */
  userId?: string | null;
  /** Live group membership — of the person, or of the agent. */
  groupIds: readonly string[];
  /** Workspace agent id, when the agent itself is asking. */
  agentId?: string | null;
}

/**
 * Highest-wins resolution across a principal's direct grant and every grant
 * held by a group they are currently a member of.
 *
 * Group membership is passed in by the caller because reef checks groups
 * *live* at access time rather than snapshotting them at share time — see
 * `@reef/authz`. An agent's membership is read the same way and for the same
 * reason: taking the agent out of a group has to revoke what that group reached
 * on its next call, not on a cache expiry.
 */
export function resolveLevel(
  grants: readonly SharingGrant[],
  subject: GrantSubject,
): PermissionLevel | null {
  const groups = new Set(subject.groupIds);
  let best: PermissionLevel | null = null;

  for (const grant of grants) {
    /*
     * Each arm compares against a field that is null for the other kind of
     * subject, so an agent never matches a `user` grant and a person never
     * matches an `agent` one — including when a grant's id was typed by hand
     * into the wrong principal, which is otherwise a grant that quietly works
     * for the wrong identity.
     */
    const applies =
      (grant.principal === 'user' && !!subject.userId && grant.id === subject.userId) ||
      (grant.principal === 'group' && groups.has(grant.id)) ||
      (grant.principal === 'agent' && !!subject.agentId && grant.id === subject.agentId);
    if (!applies) continue;
    if (best === null || RANK[grant.level] > RANK[best]) best = grant.level;
  }

  return best;
}

/**
 * The owner of a workflow always holds `admin`, independent of the grant list.
 *
 * Ownership is a person's, always: an agent has no owned set, so everything it
 * can reach it was granted. That is why revoking an agent leaves nothing
 * behind — there is no ownership floor under it to keep a workflow visible.
 */
export function resolveEffectiveLevel(
  workflow: { ownerId: string; sharing: readonly SharingGrant[] },
  subject: GrantSubject,
): PermissionLevel | null {
  if (subject.userId && workflow.ownerId === subject.userId) return 'admin';
  return resolveLevel(workflow.sharing, subject);
}
